/**
 * Task Selection Modal for BCR - allows users to select which tasks to run
 * with inline comparison configuration for change identification (supports multiple comparison types)
 */

import React, { useState, useMemo, useEffect } from 'react';
import { Modal, Checkbox, Button, Space, Alert, Divider, Select, Spin, message, Tooltip, Input, AutoComplete } from 'antd';
import { CheckOutlined, CloseOutlined, InfoCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import { BCR_TASK_CONFIGS, getBcrTaskDisplayName } from '@/config/bcrTaskConfig';
import { getPackageMcuGit } from '@/api/bcr';
import { getPackageFileTypes } from '@/api/packages';
import type { BSPPackage, BSPPackageDetail, ComparisonConfig, ComparisonConfigItem, ComparisonType, CustomBswPackage, McuGitInfo, PackageFileTypes, TypeAvailability } from '@/types';
import styles from './TaskSelectionModal.module.css';

interface TaskSelectionModalProps {
  visible: boolean;
  onConfirm: (selectedTasks: string[], comparisonConfig?: ComparisonConfig, customBswPackage?: CustomBswPackage, bvpToolsBranch?: string, showSocBspReport?: boolean, oldPackageName?: string) => void;
  onCancel: () => void;
  currentPackage?: BSPPackageDetail;
  packages: BSPPackage[];
  previousMcuGit?: McuGitInfo;  // 上一已完成任务记录的MCU Git信息，用于mcu_git对比类型
  customArtifacts?: Array<{ kind: string; name: string; url?: string }>;  // 包级别自定义制品
}

const TaskSelectionModal: React.FC<TaskSelectionModalProps> = ({
  visible: open,
  onConfirm,
  onCancel,
  currentPackage,
  packages,
  previousMcuGit,
  customArtifacts,
}) => {
  // Load from localStorage if available
  const getInitialSelection = () => {
    if (typeof window === 'undefined') {
      return BCR_TASK_CONFIGS.map(t => t.name);
    }
    try {
      const stored = localStorage.getItem('bcr_selected_tasks');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          // 过滤掉旧的任务名称，只保留配置中存在的任务
          const validNames = new Set(BCR_TASK_CONFIGS.map(t => t.name));
          const filtered = parsed.filter(name => validNames.has(name));

          // 如果解析出来的任务列表包含已删除的旧任务，则使用默认列表
          if (filtered.length > 0) {
            return filtered;
          }
        }
      }
    } catch (e) {
      console.error('Failed to load saved task selection:', e);
    }
    return BCR_TASK_CONFIGS.map(t => t.name);
  };

  const [selectedTasks, setSelectedTasks] = useState<string[]>(getInitialSelection);
  const [error, setError] = useState<string>('');
  const [bvpToolsBranch, setBvpToolsBranch] = useState<string>('');

  // Custom BSW package configuration - dynamic list of packages
  const [customBswPackage, setCustomBswPackage] = useState<CustomBswPackage>({
    items: [],
  });

  // New UI: Old package selection for comparison
  const [oldPackageId, setOldPackageId] = useState<number | null>(null);
  const [oldPackageName, setOldPackageName] = useState<string>('');
  const [loadingFileTypes, setLoadingFileTypes] = useState(false);

  // File type availability: tracks which comparison types are available
  const [typeAvailability, setTypeAvailability] = useState<Record<ComparisonType, TypeAvailability>>({
    'MCU': { available: true, reason: '' },
    'SOC_MW': { available: true, reason: '' },
    'SOC_BSP': { available: true, reason: '' },
    'MCU-GIT': { available: true, reason: '' },
    'MCU_COMMIT': { available: true, reason: '' },
  });

  // Package file types for old and new packages
  const [oldPackageFileTypes, setOldPackageFileTypes] = useState<PackageFileTypes | null>(null);
  const [newPackageFileTypes, setNewPackageFileTypes] = useState<PackageFileTypes | null>(null);

  // Comparison type selection
  const [selectedComparisonTypes, setSelectedComparisonTypes] = useState<ComparisonType[]>([]);

  // SOC_BSP 镜像文件是否新旧成对可用（仅决定要不要跑旧的镜像扫描）。
  // 注意：这个标志【不】控制复选框的 disabled——SOC_BSP 恒可勾，因为它同时是
  // 新方案「SOC底软对比报告」的展示开关，而新方案报告来自 SOC 测试、不需要包文件。
  // 文件对不齐时 buildComparisonConfig 会自动 return null 摘掉该项，静默跳过扫描。
  const [socBspFilePairAvailable, setSocBspFilePairAvailable] = useState<boolean>(false);

  // 所选旧版本的 MCU Git 信息（来自旧包最近一次跑过 MCU 构建/测试的 BCR 任务）。
  // MCU_COMMIT 的可用性应基于「所选旧版本」是否跑过 MCU（是否有 mcu_git），
  // 而非当前包自身的历史 —— 旧版本跑过 MCU 测试即有 commit 节点可对比。
  const [oldPackageMcuGit, setOldPackageMcuGit] = useState<McuGitInfo | undefined>();

  // Reset comparison config when modal closes
  useEffect(() => {
    if (!open) {
      setOldPackageId(null);
      setOldPackageName('');
      setSelectedComparisonTypes([]);
      setTypeAvailability({
        'MCU': { available: true, reason: '' },
        'SOC_MW': { available: true, reason: '' },
        'SOC_BSP': { available: true, reason: '' },
        'MCU-GIT': { available: true, reason: '' },
        'MCU_COMMIT': { available: true, reason: '' },
      });
      setOldPackageFileTypes(null);
      setNewPackageFileTypes(null);
      setCustomBswPackage({ items: [] });
      setBvpToolsBranch('');
      setSocBspFilePairAvailable(false);
      setOldPackageMcuGit(undefined);
      console.log('[TaskSelectionModal] Modal closed, reset all state');
    }
  }, [open]);

  // Load new package file types when modal opens or currentPackage changes
  useEffect(() => {
    if (open && currentPackage && selectedTasks.includes('change identification')) {
      const loadNewPackageTypes = async () => {
        try {
          const fileTypes = await getPackageFileTypes(currentPackage.id);
          console.log('[DEBUG] New package file types response:', fileTypes);
          setNewPackageFileTypes(fileTypes);
        } catch (error) {
          console.error('Failed to load new package file types:', error);
        }
      };
      loadNewPackageTypes();
    }
  }, [open, currentPackage, selectedTasks]);

  const deliverableOptions = useMemo(() => {
    const opts: { label: string; value: string }[] = [];
    // Package files
    if (currentPackage?.files?.length) {
      currentPackage.files.forEach(f => {
        if (f.file_name) {
          opts.push({ label: f.file_name, value: f.file_name });
        }
        if (f.source_type === 'artifact' && f.artifact_name && f.artifact_name !== f.file_name) {
          opts.push({ label: `${f.artifact_name} (artifact)`, value: f.artifact_name });
        }
      });
    }
    // Custom artifacts (package-level, shared across all BCR tasks for the same BSW version)
    if (customArtifacts?.length) {
      customArtifacts.forEach(a => {
        if (a.name && !opts.some(o => o.value === a.name)) {
          opts.push({ label: `${a.name} (${a.kind}·自定义)`, value: a.name });
        }
      });
    }
    return opts;
  }, [currentPackage?.files, customArtifacts]);

  const handleSelectAll = () => {
    setSelectedTasks(BCR_TASK_CONFIGS.map(t => t.name));
    setError('');
  };

  const handleClearAll = () => {
    setSelectedTasks([]);
    setError('');
  };

  const handleTaskChange = (taskName: string, checked: boolean) => {
    console.log(`[TaskSelectionModal] handleTaskChange: taskName="${taskName}", checked=${checked}, currentSelectedTasks=${JSON.stringify(selectedTasks)}`);

    let newSelectedTasks = selectedTasks;

    if (checked) {
      newSelectedTasks = [...selectedTasks, taskName];
    } else {
      newSelectedTasks = selectedTasks.filter(t => t !== taskName);
      // 当取消变更识别时，清除对比配置
      if (taskName === 'change identification') {
        setOldPackageId(null);
        setOldPackageName('');
        setSelectedComparisonTypes([]);
      }
      // 当取消烧录BSW包时，清除烧录配置
      if (taskName === 'flash bsw package') {
        console.log('[TaskSelectionModal] Unchecked flash bsw package, clearing custom BSW config');
        setCustomBswPackage({ items: [] });
      }
    }

    console.log(`[TaskSelectionModal] Updated selectedTasks to: ${JSON.stringify(newSelectedTasks)}`);
    setSelectedTasks(newSelectedTasks);
    setError('');
  };

  // Handle old package selection
  const handleOldPackageChange = async (packageId: number) => {
    const selected = packages.find(p => p.id === packageId);
    if (!selected) return;

    setOldPackageId(packageId);
    setOldPackageName(selected.name);
    setLoadingFileTypes(true);
    setSelectedComparisonTypes([]); // Clear selected types when package changes

    try {
      const fileTypes = await getPackageFileTypes(packageId);
      console.log('[DEBUG] Old package file types response:', fileTypes);
      setOldPackageFileTypes(fileTypes);

      // 获取所选旧版本的 MCU Git 信息：旧版本跑过 MCU 构建/测试即有 commit，
      // MCU_COMMIT 可用性以此为准（不依赖当前包自身的历史）。
      let fetchedMcuGit: McuGitInfo | undefined;
      try {
        const mcuGitResp = await getPackageMcuGit(packageId);
        fetchedMcuGit = mcuGitResp.mcu_git || undefined;
        setOldPackageMcuGit(fetchedMcuGit);
        console.log('[DEBUG] Old package mcu_git:', fetchedMcuGit);
      } catch (mcuErr) {
        console.error('[TaskSelectionModal] Failed to load old package mcu_git:', mcuErr);
        setOldPackageMcuGit(undefined);
      }

      console.log('[DEBUG] New package file types:', newPackageFileTypes);

      // Compute type availability
      const oldMcuSource = fetchedMcuGit ?? previousMcuGit;
      const availability: Record<ComparisonType, TypeAvailability> = {
        'MCU': { available: true, reason: '' },
        'SOC_MW': { available: true, reason: '' },
        'SOC_BSP': { available: true, reason: '' },
        'MCU-GIT': { available: true, reason: '' },
        'MCU_COMMIT': {
          available: !!oldMcuSource?.repo_url && !!oldMcuSource?.commit,
          reason: !oldMcuSource?.repo_url || !oldMcuSource?.commit
            ? '所选旧版本未找到已运行过MCU构建/测试的BCR记录（无MCU commit），无法进行MCU_COMMIT对比'
            : '',
        },
      };

      // SOC_BSP 不参与 disabled 计算（恒可勾），只单独算镜像文件是否成对
      const compareableTypes: ComparisonType[] = ['MCU', 'SOC_MW'];
      compareableTypes.forEach(type => {
        const inOld = fileTypes.available_types.includes(type);
        const inNew = newPackageFileTypes?.available_types.includes(type) ?? false;

        const oldFile = fileTypes.files[type];
        const newFile = newPackageFileTypes?.files[type];

        console.log(`[DEBUG] Type ${type}: inOld=${inOld}, inNew=${inNew}`, {
          oldTypes: fileTypes.available_types,
          newTypes: newPackageFileTypes?.available_types,
          oldSourceType: oldFile?.source_type,
          newSourceType: newFile?.source_type,
        });

        if (!inOld && !inNew) {
          availability[type] = { available: false, reason: '新旧版本包均缺少此文件类型' };
        } else if (!inOld) {
          availability[type] = { available: false, reason: '旧版本包缺少此文件类型' };
        } else if (!inNew) {
          availability[type] = { available: false, reason: '新版本包缺少此文件类型' };
        } else if (!oldFile || !newFile) {
          // This should not happen if inOld and inNew are true, but add guard for TypeScript
          availability[type] = { available: false, reason: '无法获取文件信息' };
        } else if (type === 'MCU') {
          // MCU支持Git和文件两种方式
          const oldIsGit = oldFile.source_type === 'git';
          const newIsGit = newFile.source_type === 'git';
          const oldIsFile = oldFile.source_type === 'upload' || oldFile.source_type === 'select';
          const newIsFile = newFile.source_type === 'upload' || newFile.source_type === 'select';

          // 要么都是Git，要么都是文件，不能混合
          if ((oldIsGit && newIsGit) || (oldIsFile && newIsFile)) {
            availability[type] = { available: true, reason: '' };
          } else {
            availability[type] = {
              available: false,
              reason: `MCU新旧版本上传方式不一致(旧:${oldFile.source_type}, 新:${newFile.source_type}，需都为Git或都为文件)`
            };
          }
        } else if (type === 'SOC_MW' || type === 'SOC_BSP') {
          // SOC_MW和SOC_BSP只支持文件方式(upload或select)
          const oldIsFile = oldFile.source_type === 'upload' || oldFile.source_type === 'select';
          const newIsFile = newFile.source_type === 'upload' || newFile.source_type === 'select';

          if (!oldIsFile || !newIsFile) {
            availability[type] = {
              available: false,
              reason: `${type}当前仅支持文件对比,不支持Git方式`
            };
          } else {
            availability[type] = { available: true, reason: '' };
          }
        } else {
          availability[type] = { available: true, reason: '' };
        }
      });

      // SOC_BSP 镜像扫描的前置条件：新旧包都有 SOC_BSP 文件，且两侧都是文件方式
      // （upload/select）。仅用于 tooltip 提示，不影响复选框可勾。
      const isFileMode = (f?: { source_type?: string }) =>
        f?.source_type === 'upload' || f?.source_type === 'select';
      const oldSocBsp = fileTypes.files['SOC_BSP'];
      const newSocBsp = newPackageFileTypes?.files['SOC_BSP'];
      setSocBspFilePairAvailable(
        !!oldSocBsp && !!newSocBsp && isFileMode(oldSocBsp) && isFileMode(newSocBsp),
      );

      console.log('[DEBUG] Final type availability:', availability);
      setTypeAvailability(availability);
    } catch (error) {
      console.error('Failed to load old package file types:', error);
      message.error('加载旧版本包文件类型失败');
    } finally {
      setLoadingFileTypes(false);
    }
  };

  // Handle comparison type selection
  const handleComparisonTypeChange = (types: (string | number)[]) => {
    setSelectedComparisonTypes(types as ComparisonType[]);
  };

  const handleConfirm = async () => {
    if (selectedTasks.length === 0) {
      setError('至少需要选择1个任务');
      return;
    }

    // 依赖台架的任务必须勾选「预约台架」
    if (needReserveBenchWarning) {
      setError(needReserveBenchWarning);
      return;
    }

    // 如果选中了变更识别，需要验证对比配置
    let comparisonConfig: ComparisonConfig | undefined;
    if (selectedTasks.includes('change identification')) {
      if (!oldPackageId) {
        setError('选择了变更识别，需要选择旧版本底软包');
        return;
      }
      if (selectedComparisonTypes.length === 0) {
        setError('请至少选择一个对比类型');
        return;
      }

      // 为每个选中的对比类型构建配置
      comparisonConfig = selectedComparisonTypes
        .map(type => {
          const config: ComparisonConfigItem = { type };

          if (['MCU', 'SOC_MW', 'SOC_BSP'].includes(type)) {
            const sourceFile = oldPackageFileTypes?.files[type];
            const targetFile = newPackageFileTypes?.files[type];

            // 如果文件不存在,返回 null 而不是抛出错误
            if (!sourceFile || !targetFile) {
              console.warn(`[BCR] Missing files for ${type} comparison, skipping`);
              return null;
            }

            // 根据source_type选择构建方式
            const sourceIsGit = sourceFile.source_type === 'git';
            const targetIsGit = targetFile.source_type === 'git';

            if (type === 'MCU' && sourceIsGit && targetIsGit) {
              // MCU-GIT模式：使用Git信息
              if (!sourceFile.git_url || !targetFile.git_url ||
                  !sourceFile.git_branch || !targetFile.git_branch ||
                  !sourceFile.git_commit || !targetFile.git_commit) {
                console.warn(`[BCR] Incomplete Git info for MCU comparison, skipping`);
                return null;
              }

              // 返回MCU-GIT类型配置
              return {
                type: 'MCU-GIT',
                source_git: {
                  repo_url: sourceFile.git_url,
                  branch: sourceFile.git_branch,
                  commit: sourceFile.git_commit,
                },
                target_git: {
                  repo_url: targetFile.git_url,
                  branch: targetFile.git_branch,
                  commit: targetFile.git_commit,
                },
              };
            } else {
              // 文件模式(upload或select)：使用file_url
              if (!sourceFile.file_url || !targetFile.file_url) {
                console.warn(`[BCR] Empty file_url for ${type} comparison, skipping`);
                return null;
              }

              config.source = {
                package_name: oldPackageName,
                file_url: sourceFile.file_url,
                file_name: sourceFile.file_name,
              };
              config.target = {
                file_url: targetFile.file_url,
                file_name: targetFile.file_name,
              };
            }
          }

          if (type === 'MCU_COMMIT') {
            // source = 所选旧版本的 MCU Git 信息（旧包最近跑过 MCU 的 BCR 任务），
            // 无旧包记录时回退到上一轮当前包任务的 mcu_git
            const oldMcuSource = oldPackageMcuGit ?? previousMcuGit;
            if (!oldMcuSource?.repo_url || !oldMcuSource?.branch || !oldMcuSource?.commit) {
              console.warn('[BCR] Missing previous mcu_git info for MCU_COMMIT comparison, skipping');
              return null;
            }
            return {
              type: 'MCU_COMMIT',
              source_git: {
                repo_url: oldMcuSource.repo_url,
                branch: oldMcuSource.branch,
                commit: oldMcuSource.commit,
              },
              // target_git 由后端从 ctx 和 git ls-remote 补全，前端只需 source_git
            };
          }

          return config;
        })
        .filter((cfg): cfg is ComparisonConfigItem => cfg !== null);  // 过滤掉 null 值

      // 提交前验证对比配置完整性。
      // SOC_BSP 兼作新方案报告的展示开关：即便镜像文件对不齐（不产出 config 项），
      // 勾选本身仍有意义，不能因此阻断创建——bsp_back 允许 comparison_config 为空数组
      // （apps/bcr/serializers.py:92-99）。只有被摘掉的类型里还有别的才报错。
      const nonSocBspSelected = selectedComparisonTypes.filter(t => t !== 'SOC_BSP');
      if (comparisonConfig.length === 0 && nonSocBspSelected.length > 0) {
        setError('所选的对比类型都无法使用，请检查文件是否存在');
        return;
      }

      // 验证对比配置中的数据完整性
      const invalidConfigs = comparisonConfig.filter(cfg => {
        if (cfg.type === 'MCU-GIT') {
          return !cfg.source_git || !cfg.target_git;
        } else if (['MCU', 'SOC_MW', 'SOC_BSP'].includes(cfg.type)) {
          return !cfg.source || !cfg.target || !cfg.source.file_url || !cfg.target.file_url;
        }
        return false;
      });

      if (invalidConfigs.length > 0) {
        setError('对比配置不完整，请检查选择的文件');
        return;
      }
    }

    // Validate custom BSW package items
    const hasCustomPackage = customBswPackage.items && customBswPackage.items.length > 0;

    if (hasCustomPackage) {
      console.log(`[TaskSelectionModal] Validating custom BSW packages:`, customBswPackage);

      // 检查是否有重复的包类型
      const types = customBswPackage.items.map(item => item.package_type);
      const uniqueTypes = new Set(types);
      if (types.length !== uniqueTypes.size) {
        setError('包类型重复，请检查每个包的类型是否唯一');
        return;
      }

      // 验证每个包的文件名格式
      const fileExtRegex = /\.(tgz|tar\.gz|zip|7z)$/i;
      for (const item of customBswPackage.items) {
        if (!item.package_name || !item.package_name.match(fileExtRegex)) {
          setError(`${item.package_type}包文件名必须以 .tgz / .tar.gz / .zip / .7z 结尾`);
          return;
        }
      }

      // 必须勾选"烧录BSW包"任务
      if (!selectedTasks.includes('flash bsw package')) {
        setError('指定了自定义BSW包但未选择"烧录BSW包"任务');
        return;
      }
    }

    // ⭐ 关键验证：如果勾选了"烧录BSW包"但没有包来源，阻止创建任务
    if (selectedTasks.includes('flash bsw package')) {
      const hasCustomPackageForFlash = customBswPackage.items && customBswPackage.items.length > 0;
      const hasBuildTask = selectedTasks.includes('building bsw package');

      if (!hasCustomPackageForFlash && !hasBuildTask) {
        message.error(
          '勾选了"烧录BSW包"但未指定包来源：请填写自定义包配置或勾选"构建BSW包"任务'
        );
        return;
      }
    }

    try {
      localStorage.setItem('bcr_selected_tasks', JSON.stringify(selectedTasks));
    } catch (e) {
      console.error('Failed to save task selection:', e);
    }

    // Only pass customBswPackage if at least one package is specified
    const finalCustomBswPackage = hasCustomPackage ? customBswPackage : undefined;

    // 勾选 SOC_BSP 同时做两件事：
    //   1. 进 comparison_config（上面已构建），触发 bvp_back 的旧镜像扫描
    //   2. 存进后端 show_soc_bsp_report，详情页据此展示 SOC 测试产出的底软对比报告
    onConfirm(
      selectedTasks,
      comparisonConfig,
      finalCustomBswPackage,
      bvpToolsBranch || undefined,
      selectedComparisonTypes.includes('SOC_BSP'),
      oldPackageName || undefined,
    );
  };

  // 依赖提示：order>=3 的任务依赖「预约台架」设置 bench_name
  const needReserveBenchWarning = useMemo(() => {
    const hasReserveBench = selectedTasks.includes('reserve bench');
    if (hasReserveBench) return '';
    const hasDependent = selectedTasks.some(t => {
      const cfg = BCR_TASK_CONFIGS.find(c => c.name === t);
      return cfg && cfg.order >= 3;
    });
    return hasDependent ? '请勾选【预约台架】' : '';
  }, [selectedTasks]);

  const totalEstimatedTime = useMemo(() => {
    const selected = BCR_TASK_CONFIGS.filter(t => selectedTasks.includes(t.name));
    if (selected.length === 0) return '--';

    let minTotal = 0;
    let maxTotal = 0;

    selected.forEach(task => {
      const match = task.estimatedTime.match(/(\d+)-(\d+)/);
      if (match) {
        minTotal += parseInt(match[1], 10);
        maxTotal += parseInt(match[2], 10);
      }
    });

    return `${minTotal}-${maxTotal}分钟`;
  }, [selectedTasks]);

  return (
    <Modal
      title="选择BCR子任务"
      open={open}
      onCancel={onCancel}
      width={900}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          取消
        </Button>,
        <Button key="confirm" type="primary" onClick={handleConfirm}>
          确定
        </Button>
      ]}
    >
      <div className={styles.container}>
        {/* Error message */}
        {error && (
          <Alert
            title={error}
            type="error"
            showIcon
            closable
            onClose={() => setError('')}
            style={{ marginBottom: '16px' }}
          />
        )}

        {/* Quick select buttons */}
        <div style={{ marginBottom: '16px' }}>
          <Space>
            <Button
              type="dashed"
              size="small"
              icon={<CheckOutlined />}
              onClick={handleSelectAll}
            >
              全选
            </Button>
            <Button
              type="dashed"
              size="small"
              icon={<CloseOutlined />}
              onClick={handleClearAll}
            >
              全不选
            </Button>
          </Space>
        </div>

        <Divider style={{ margin: '12px 0' }} />

        {/* Task list with inline change identification config */}
        <div className={styles.taskList}>
          {BCR_TASK_CONFIGS.map(task => {
            const isSelected = selectedTasks.includes(task.name);
            const isChangeIdentification = task.name === 'change identification';

            return (
              <React.Fragment key={task.name}>
                {/* Task checkbox row */}
                <div className={styles.taskItem}>
                  <div className={styles.taskRow}>
                    <div className={styles.taskCheckbox}>
                      <Checkbox
                        checked={isSelected}
                        onChange={e => handleTaskChange(task.name, e.target.checked)}
                      >
                        <span className={styles.taskLabel}>{getBcrTaskDisplayName(task.name)}</span>
                      </Checkbox>
                    </div>
                    <div className={styles.taskDescription}>{task.description}</div>
                  </div>
                </div>

                {/* Change Identification Config Sub-panel (inline, nested) */}
                {isChangeIdentification && isSelected && (
                  <div style={{
                    marginLeft: '32px',
                    marginTop: '8px',
                    marginBottom: '12px',
                    padding: '12px',
                    backgroundColor: '#fafafa',
                    borderRadius: '4px',
                    borderLeft: '3px solid #1890ff'
                  }}>
                    <div style={{ marginBottom: '12px', fontWeight: 500, color: '#1890ff', fontSize: '14px' }}>
                      ⚙️ 对比配置
                    </div>

                    {/* Old Package Selection */}
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 500 }}>
                        旧版本底软包 *
                      </label>
                      <Spin spinning={loadingFileTypes}>
                        <Select
                          placeholder="选择旧版本底软包"
                          style={{ width: '100%' }}
                          showSearch
                          optionFilterProp="label"
                          value={oldPackageId}
                          onChange={handleOldPackageChange}
                          options={packages
                            .filter(p => p.id !== currentPackage?.id)
                            .map(p => ({ label: p.name, value: p.id }))}
                        />
                      </Spin>
                    </div>

                    {/* Comparison Type Selection */}
                    {oldPackageId && (
                      <div>
                        <div style={{ marginBottom: '8px', fontSize: '13px', fontWeight: 500 }}>
                          对比类型（可多选）
                        </div>
                        <Checkbox.Group
                          value={selectedComparisonTypes}
                          onChange={handleComparisonTypeChange}
                        >
                          <Space orientation="vertical">
                            {(['MCU', 'SOC_MW', 'SOC_BSP', 'MCU_COMMIT'] as ComparisonType[]).map(type => {
                              const availability = typeAvailability[type];
                              // SOC_BSP 恒可勾（它同时是新方案报告的展示开关）。
                              // 镜像文件对不齐时不禁用，只提示会跳过镜像扫描。
                              const socBspWillSkipScan =
                                type === 'SOC_BSP' && !socBspFilePairAvailable;
                              const tooltip = socBspWillSkipScan
                                ? '包内缺少 SOC 镜像文件，将跳过镜像扫描；SOC 测试完成后仍会显示底软对比报告'
                                : (availability.available ? '' : availability.reason);
                              return (
                                <div key={type}>
                                  <Tooltip title={tooltip}>
                                    <Checkbox
                                      value={type}
                                      disabled={!availability.available}
                                    >
                                      {type}
                                      {(!availability.available || socBspWillSkipScan) && (
                                        <InfoCircleOutlined
                                          style={{ marginLeft: 8, color: '#999', fontSize: 14 }}
                                        />
                                      )}
                                    </Checkbox>
                                  </Tooltip>
                                </div>
                              );
                            })}
                          </Space>
                        </Checkbox.Group>

                        {/* Warning Alert */}
                        {!typeAvailability.MCU.available
                          && !typeAvailability.SOC_MW.available
                          && !socBspFilePairAvailable && (
                          <Alert
                            type="warning"
                            title="选中的新旧版本底软包没有可对比的文件类型，请选择其他旧版本包"
                            showIcon
                            style={{ marginTop: '12px', fontSize: '12px' }}
                          />
                        )}
                      </div>
                )}

                  </div>
                )}

                {/* Flash BSW Package Config Sub-panel */}
                {task.name === 'flash bsw package' && isSelected && (
                  <div style={{
                    marginLeft: '32px',
                    marginTop: '8px',
                    marginBottom: '12px',
                    padding: '12px',
                    backgroundColor: '#fafafa',
                    borderRadius: '4px',
                    borderLeft: '3px solid #faad14'
                  }}>
                    {(() => {
                      console.log('[TaskSelectionModal] Rendering Flash BSW Package Config Panel');
                      return null;
                    })()}
                    <div style={{ marginBottom: '12px', fontWeight: 500, color: '#faad14', fontSize: '14px' }}>
                      ⚙️ 烧录配置
                    </div>

                    {/* BSW Package List */}
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ marginBottom: '8px', fontSize: '13px', fontWeight: 500 }}>
                        烧录包配置（可选）
                        <Tooltip title="如果需要指定烧录包，点击'增加烧录包'添加需要烧录的包。同一种类型最多只能添加1个包">
                          <InfoCircleOutlined style={{ marginLeft: 4 }} />
                        </Tooltip>
                      </div>

                      {/* Package List */}
                      {customBswPackage.items && customBswPackage.items.length > 0 && (
                        <div style={{ marginBottom: '12px' }}>
                          {customBswPackage.items.map((item, idx) => (
                            <div key={`package-item-${item.package_type}-${idx}`} style={{
                              display: 'flex',
                              gap: '8px',
                              marginBottom: '8px',
                              padding: '8px',
                              backgroundColor: '#f5f5f5',
                              borderRadius: '4px',
                              alignItems: 'flex-start'
                            }}>
                              {/* Package Type Select */}
                              <Select
                                style={{ flex: '0 0 150px' }}
                                value={item.package_type}
                                onChange={(value) => {
                                  const updated = [...customBswPackage.items];
                                  updated[idx].package_type = value;
                                  setCustomBswPackage({ items: updated });
                                }}
                                options={[
                                  { label: 'BSW',      value: 'BSW' },
                                  { label: 'MCU',      value: 'MCU' },
                                  { label: 'SWITCH',   value: 'SWITCH' },
                                  { label: 'BSW-SOC',  value: 'BSW-SOC' },
                                  { label: 'BSP',      value: 'BSP' },
                                  { label: 'MF',       value: 'MF' },
                                  { label: 'ADCU_ALL', value: 'ADCU_ALL' },
                                  { label: 'MCU0',     value: 'MCU0' },
                                  { label: 'MCU1',     value: 'MCU1' },
                                  { label: 'SWITCH0',  value: 'SWITCH0' },
                                  { label: 'SWITCH_1', value: 'SWITCH_1' },
                                ]}
                                placeholder="选择包类型"
                              />

                              {/* Custom Type Input (if "other" is selected) */}
                              {item.package_type && !['BSW', 'MCU', 'SWITCH', 'BSW-SOC', 'BSP', 'MF', 'ADCU_ALL', 'MCU0', 'MCU1', 'SWITCH0', 'SWITCH_1'].includes(item.package_type) && (
                                <Input
                                  style={{ flex: '0 0 100px' }}
                                  placeholder="自定义类型"
                                  value={item.package_type}
                                  onChange={(e) => {
                                    const updated = [...customBswPackage.items];
                                    updated[idx].package_type = e.target.value.trim();
                                    setCustomBswPackage({ items: updated });
                                  }}
                                />
                              )}

                              {/* Package Name Input */}
                              <AutoComplete
                                style={{ flex: 1 }}
                                placeholder="输入包文件名（如：package.tgz）"
                                value={item.package_name}
                                options={deliverableOptions}
                                filterOption={(inputValue, option) =>
                                  option!.value.toLowerCase().includes(inputValue.toLowerCase())
                                }
                                onChange={(value) => {
                                  const updated = [...customBswPackage.items];
                                  updated[idx].package_name = value;
                                  setCustomBswPackage({ items: updated });
                                }}
                                allowClear
                              />

                              {/* Delete Button */}
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => {
                                  const updated = customBswPackage.items.filter((_, i) => i !== idx);
                                  setCustomBswPackage({ items: updated });
                                }}
                                style={{ flex: '0 0 40px' }}
                              />
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add Button */}
                      <Button
                        type="dashed"
                        block
                        onClick={() => {
                          setCustomBswPackage({
                            items: [...(customBswPackage.items || []), { package_type: '', package_name: '' }]
                          });
                        }}
                      >
                        + 增加烧录包
                      </Button>
                    </div>

                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        <Divider style={{ margin: '12px 0' }} />

        {/* bvp_tools branch input — 仅非生产环境显示 */}
        {window.location.hostname !== 'bsp.momenta.works' && (
          <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ whiteSpace: 'nowrap' }}>bvp_tools分支：</span>
            <Input
              placeholder="dev"
              value={bvpToolsBranch}
              onChange={e => setBvpToolsBranch(e.target.value.trim())}
              style={{ width: 200 }}
              allowClear
            />
            <span style={{ color: '#8c8c8c', fontSize: '12px' }}>可选，留空则使用默认 dev 分支</span>
          </div>
        )}

        {/* Summary */}
        <div className={styles.summary}>
          <div>
            已选择: <strong>{selectedTasks.length}</strong> 个任务
          </div>
          <div>
            预估总耗时: <strong>{totalEstimatedTime}</strong>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default TaskSelectionModal;
